import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/internal/operators/tap';
import { AuthUserDto } from '../../shared/models/Security/AuthUserDto';
import { LoginDto } from '../../shared/models/LoginDto';
import { environment } from '../../../environments/environment';
import { ActionDataResult } from '../../shared/models/core/ActionDataResult';
import { EnableOtpLoginResponse } from '../../shared/models/Security/enable-otp-login-response';
import { VerifyOtpLoginRequest } from '../../shared/models/Security/verify-otp-login-request';
import { VerifyOtpLoginResponse } from '../../shared/models/Security/verify-otp-login-response';
import { ActionResult } from '../../shared/models/core/ActionResult';
import { map } from 'rxjs/operators';
import { SessionUserStorageItem } from '../../shared/models/Security/session-user-storage-item';

const httpOptions = {
	headers: new HttpHeaders({
		'Content-Type': 'application/json'
	})
};

@Injectable({
	providedIn: 'root'
})
export class SecurityService {
	_currentUser: AuthUserDto = new AuthUserDto();

	private sessionUsersStorageKey = 'session-users';
	private sessionUserRoles = ['IsLaborant'];

	get currentUser(): AuthUserDto {
		return this._currentUser;
	}

	set currentUser(value: AuthUserDto) {
		this._currentUser = value;
	}

	constructor(private http: HttpClient) {
	}

	login(entity: LoginDto): Observable<ActionDataResult<AuthUserDto>> {
		if (!entity.switchRequest) {
			// Initialize security object
			this.resetSecurityObject();
		}

		const apiUrl = environment.apiUrl + 'v1/security/login';

		return this.http.post<ActionDataResult<AuthUserDto>>(apiUrl, entity, httpOptions).pipe(
			tap(res => {

				if (res.isSuccessful) {
					if (entity.switchRequest) {
						this.resetSecurityObject();
					}

					this._currentUser = res.data;
					localStorage.setItem('authUser', JSON.stringify(this._currentUser));
					this.saveSessionUser(res.data);
				}

			}));
	}

	enableOtpLogin(entity: LoginDto): Observable<ActionDataResult<EnableOtpLoginResponse>> {
		const apiUrl = environment.apiUrl + 'v1/security/enable-otp-login';
		return this.http.post<ActionDataResult<EnableOtpLoginResponse>>(apiUrl, entity, httpOptions);
	}

	verifyOtpLogin(entity: VerifyOtpLoginRequest): Observable<ActionDataResult<VerifyOtpLoginResponse>> {
		const apiUrl = environment.apiUrl + 'v1/security/verify-otp-login';

		return this.http.post<ActionDataResult<VerifyOtpLoginResponse>>(apiUrl, entity, httpOptions).pipe(
			tap(res => {
				if (res.isSuccessful && res.data.otpCodeIsValid) {
					this._currentUser = res.data.authUser;
					localStorage.setItem('authUser', JSON.stringify(this._currentUser));
					this.saveSessionUser(res.data.authUser);
				}
			})
		);
	}

	generateOtpToken(userId: number): Observable<ActionResult> {
		const apiUrl = environment.apiUrl + 'v1/security/generate-otp-token';
		return this.http.post<ActionResult>(apiUrl, {
			userId: userId,
			regenerateToken: true
		}, httpOptions)
	}

	sendOtpTokenToUser(userId: number): Observable<any> {
		const apiUrl = environment.apiUrl + 'v1/security/send-otp-token-to-user';
		return this.http.post(apiUrl, 
			{ userId: userId, }, 
			{ responseType: 'arraybuffer', headers: httpOptions.headers })
		.pipe(
			map(response => {
				try {
					const decoder = new TextDecoder('utf-8');
					const jsonString = decoder.decode(response);
					return JSON.parse(jsonString);
				} catch (e) {
					return new Blob([response]);
				}
			})
		); // TODO: временное решение!! потом не будет возвращать ничего. Хороший пример обработки файлового ответа
	}

	logout(): void {
		this.resetSecurityObject();
	}

	resetAuthUser(bearerToken: string) {
		var httpOptions = {
			headers: new HttpHeaders({
				'Authorization': `Bearer ${bearerToken}`,
			})
		};

		return this.http.get<ActionDataResult<AuthUserDto>>(environment.apiUrl + 'v1/security/auth-user', httpOptions).pipe(
			tap(resp => {
				if (resp.isSuccessful) {
					this._currentUser = resp.data;
					localStorage.setItem('authUser', JSON.stringify(this._currentUser));
					this.saveSessionUser(resp.data);
				}
			})
		);
	}

	resetSecurityObject(): void {

		if (this._currentUser) {
			this._currentUser.userName = '';
			this._currentUser.bearerToken = '';
			this._currentUser.isAuthenticated = false;
			this._currentUser.claims = [];
		}

		localStorage.removeItem('authUser');
	}

	isClient(): boolean {
		return this.isClaimValid('IsClient');
	}

	isOnlyClient(): boolean {
		return this.isClaimValid('IsOnlyClient');
	}

	isOnlyStorekeeper(): boolean {
		return this.isClaimValid('isOnlyStorekeeper');
	}

	isEngineer(): boolean {
		return this.isClaimValid('IsEngineer');
	}
	isCostVisible(): boolean {
		return this.isSpecialRightClaimValid('DisplayTheCostOfExchangeRequests');
	}

	canViewEmployeeRoutes(): boolean {
		return this.isSpecialRightClaimValid('ViewEmployeeRoutes');
	}

	isOnlyEngineer(): boolean {
		return this.isClaimValid('IsOnlyEngineer');
	}
	isOnlySCPartnerEngineer(): boolean {
		return this.isClaimValid('IsOnlySCPartnerEngineer');
	}
	
	isAdmin(): boolean {
		return this.isClaimValid('IsAdmin');
	}

	isSCUser(): boolean {
		return this.isClaimValid('IsSCUser');
	}

	isLanterCoordinator(): boolean {
	  return this.isClaimValid('IsLanterCoordinator');
	}
	
	isSCPartnerCoordinator(): boolean {
		return this.isSpecialRightClaimValid('PartnerCoordinator');
	}

	isCanEditRequestDevices(): boolean {
		return this.isSpecialRightClaimValid('CanEditRequestDevices');
	}

	isCanEditRequestActivities(): boolean {
		return this.isSpecialRightClaimValid('CanEditRequestActivities');
	}

	isCanChangeRequestStatus(): boolean {
		return this.isSpecialRightClaimValid('CanChangeRequestStatus');
	}

	isSCPartnerEngineer(): boolean {
		return this.isSpecialRightClaimValid('PartnerServiceCenterEngineer');
	}

	isPartialDeviceReturn(): boolean {
		return this.isSpecialRightClaimValid('PartialDeviceReturn');
	}

	isStorekeeper(): boolean {
		return this.isClaimValid('IsStorekeeper');
	}

	isSelfEmployedEngineer(): boolean {
		return this.isClaimValid("IsSelfEmployedEngineer");
	}

	isServiceBoundariesFilter(): boolean {
		return this.isSpecialRightClaimValid('ServiceBoundariesFilter');
	}

	canEditRequestPerformerInSelectedSpecStates(): boolean {
		return this.isSpecialRightClaimValid('EditRequestPerformerInSelectedSpecStates');
	}

	canPlanVisitWithoutPerformerSelection(): boolean {
		return this.isSpecialRightClaimValid('PlanVisitWithoutPerformerSelection');
	}

	isCallCenterEmployee(): boolean {
		return this.isSpecialRightClaimValid('CallCenterEmployee');
	}

	canMakingCallFromRequestTsp(): boolean {
		return this.isSpecialRightClaimValid('MakingCallFromRequestTsp');
	}

	isPhoneVisibileInMobileVersion(): boolean {
		return this.isSpecialRightClaimValid('PhoneVisibilityMobileVersion');
	}	
		
	canEditTspContacts(): boolean {
		return this.isSpecialRightClaimValid('EditTspContacts');
	}
	
	canEditingEquipmentInProgressInRequest(): boolean {
		return this.isSpecialRightClaimValid('EditingEquipmentInProgressInRequest');
	}

	hasClaim(claimKey: any, claimValue?: any): boolean {
		let ret = false;

		if (this.isClaimValid('IsAdmin')) {
			return true;
		}

		// See if an array of values was passed in.
		if (typeof claimKey === 'string') {
			ret = this.isClaimValid(claimKey, claimValue);
		} else {
			const claims: string[] = claimKey;
			if (claims) {
				for (let index = 0; index < claims.length; index++) {
					ret = this.isClaimValid(claims[index]);
					// If one is successful, then let them in
					if (ret) {
						break;
					}
				}
			}
		}

		return ret;
	}

	public isSpecialRightClaimValid(key: string): boolean {
		let claimKey = `role-${key}-right`;
		return this.isClaimValid(claimKey);
	}
	
	private isClaimValid(claimKey: string, claimValue?: string): boolean {
		let ret = false;
		let auth: AuthUserDto = null;

		// Retrieve security object
		auth = this._currentUser;
		if (auth) {
			// See if the claim type has a value
			// *hasClaim="'claimType:value'"
			if (claimKey.indexOf(':') >= 0) {
				const words: string[] = claimKey.split(':');
				claimKey = words[0].toLowerCase();
				claimValue = words[1];
			} else {
				claimKey = claimKey.toLowerCase();
				// Either get the claim value, or assume 'true'
				claimValue = claimValue ? claimValue : 'true';
			}
			// Attempt to find the claim
			ret = auth.claims.find(c =>
				c.key.toLowerCase() == claimKey &&
				c.value == claimValue) != null;
		}

		return ret;
	}

	public check(): Observable<ActionDataResult<boolean>> {
		return this.http.post<ActionDataResult<boolean>>(environment.apiUrl + 'v1/security/check', null);;
	}

	public verifyUserPassword(password: string, bearerToken: string): Observable<ActionDataResult<boolean>> {
		var httpOptions = {
			headers: new HttpHeaders({
				'Authorization': `Bearer ${bearerToken}`,
			})
		};

		return this.http.post<ActionDataResult<boolean>>(environment.apiUrl + 'v1/security/verify-password', { 
			password: password
		}, httpOptions);
	}

	public getSessionUsers(): SessionUserStorageItem[] {
		var sessionUsersJson = localStorage.getItem(this.sessionUsersStorageKey);
		var sessionUsers = sessionUsersJson == null || sessionUsersJson.trim() == ''
			? [] as SessionUserStorageItem[]
			: JSON.parse(sessionUsersJson) as SessionUserStorageItem[];	

		return sessionUsers.filter(x => 
			new Date(x.expiresInUtc) > this.getCurrentUtc() && x.login != this._currentUser.login);
	}

	private saveSessionUser(user: AuthUserDto) {
		let needToSave = user.claims.some(x => 
			this.sessionUserRoles.some(r => r == x.key) && x.value == 'true');

		if (needToSave) {
			var sessionUsers = this.getSessionUsers();
			sessionUsers = sessionUsers.filter(x => 
				x.login != user.login && new Date(x.expiresInUtc) > this.getCurrentUtc());
			
			var newSessionUser = {
				login: user.login,
				expiresInUtc: user.expiresInUtc,
				bearerToken: user.bearerToken,
				userName: user.userName
			} as SessionUserStorageItem;

			sessionUsers.push(newSessionUser);

			localStorage.setItem(this.sessionUsersStorageKey, JSON.stringify(sessionUsers));
		}
	}

	private getCurrentUtc(): Date {
		const now = new Date();
		const utcCurrent = new Date(now.getTime() + now.getTimezoneOffset() * 60000);

		return utcCurrent;
	}
}
